import { useLingui } from "@lingui/react";
import {
	AGENT_LANGUAGE_CODES,
	GPT_LIVE_DEFAULT_REASONING_MODEL,
	GPT_LIVE_DEFAULT_VOICE,
	GPT_LIVE_VOICES,
	getAgentInboundAnsweringPolicy,
	getAgentTransferDestinations,
	getDefaultVoiceForModel,
	getSupportedVoicesForModel,
	isSipUri,
	isVoiceSupportedForModel,
	REALTIME_MODEL_OPTIONS,
	shortUuid,
	type TransferToHumanDestination,
	type WeeklyScheduleDay,
} from "@repo/common";
import { AlertCircle, Info, Loader2, Phone, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useData } from "../contexts/DataContext";
import { useOutboundPlaybookFeatureFlag } from "../hooks/useOutboundPlaybookFeatureFlag";
import type { Agent } from "../types";
import {
	formatPhoneNumber,
	formatPhoneNumberList,
} from "../utils/formatPhoneNumber";
import {
	normalizePhoneNumberForStorage,
	normalizeTransferTargetForStorage,
} from "../utils/phoneNormalization";
import { hasSuccessfulTestingDeployment } from "../utils/testingDeployment";
import { getContentFieldProps as buildContentFieldProps } from "../utils/textDirection";
import { OutboundPlaybooksPanel } from "./OutboundPlaybooksPage";
import { SettingsGroup } from "./SettingsGroup";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";

type PolicySection = NonNullable<Agent["policySections"]>[number];
type Message = NonNullable<Agent["messages"]>[number];
type Flow = NonNullable<Agent["typicalFlows"]>[number];

interface AgentEditorProps {
	mode: "create" | "edit";
	agent: Agent;
	onChange: (agent: Agent) => void;
	onCancel: () => void;
	onSave: () => void;
	isSaveDisabled?: boolean;
	isSaving?: boolean;
	hasNewerVersion?: boolean;
	canEditAdminFields?: boolean;
}

const REALTIME_EFFORT_OPTIONS = [
	"minimal",
	"low",
	"medium",
	"high",
	"xhigh",
] as const;

const FALLBACK_TIME_ZONE_OPTIONS = [
	"Asia/Jerusalem",
	"UTC",
	"Europe/London",
	"Europe/Paris",
	"Europe/Berlin",
	"Asia/Dubai",
	"America/Toronto",
	"America/New_York",
	"America/Los_Angeles",
] as const;
const TIME_ZONE_OPTIONS = [
	...new Set([
		"Asia/Jerusalem",
		"UTC",
		...(typeof Intl.supportedValuesOf === "function"
			? Intl.supportedValuesOf("timeZone")
			: FALLBACK_TIME_ZONE_OPTIONS),
	]),
];
const fieldClassName =
	"border-border/80 focus-visible:ring-1 focus-visible:ring-gray-300";
const WEEKDAYS: WeeklyScheduleDay[] = [
	"sunday",
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
];
const DEFAULT_AVAILABILITY_WINDOW = {
	days: [
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
	] as WeeklyScheduleDay[],
	startTime: "09:00",
	endTime: "17:00",
};

function EditorLabel({
	className = "",
	...props
}: React.ComponentProps<"label">) {
	return (
		<label
			className={`block w-full text-start text-sm font-medium ${className}`.trim()}
			{...props}
		/>
	);
}

export function AgentEditor({
	mode,
	agent,
	onChange,
	onCancel,
	onSave,
	isSaveDisabled = false,
	isSaving = false,
	hasNewerVersion = false,
	canEditAdminFields = false,
}: AgentEditorProps) {
	const { i18n } = useLingui();
	const agentLanguageLabels = {
		he: i18n._("agentEditor.languages.he"),
		en: i18n._("agentEditor.languages.en"),
		es: i18n._("agentEditor.languages.es"),
		fr: i18n._("agentEditor.languages.fr"),
		de: i18n._("agentEditor.languages.de"),
		it: i18n._("agentEditor.languages.it"),
		ru: i18n._("agentEditor.languages.ru"),
		ar: i18n._("agentEditor.languages.ar"),
	};
	const { services, language, user, companyProfile, testingPhoneNumbers } =
		useData();
	const outboundPlaybooksEnabled = useOutboundPlaybookFeatureFlag(
		user?.companyId,
		companyProfile,
	);
	const [blacklistInput, setBlacklistInput] = useState("");
	const [testerInput, setTesterInput] = useState("");
	const [transferInput, setTransferInput] = useState("");
	const [transferPriority, setTransferPriority] = useState(1);
	const [hasSavedPlaybookChanges, setHasSavedPlaybookChanges] = useState(false);
	const isHebrew = language === "he";
	const isRtl = language === "he";
	const defaultTextDirection = isRtl ? "rtl" : "ltr";
	const editorFieldClassName = `${fieldClassName} text-start`;
	const getContentFieldProps = (
		value: string | null | undefined,
		extraClassName = "",
	) =>
		buildContentFieldProps(
			value,
			defaultTextDirection,
			`${fieldClassName} ${extraClassName}`,
		);
	const fieldGroupClassName = isRtl
		? "space-y-2 text-start [direction:rtl]"
		: "space-y-2 text-start";
	const fieldLabelClassName = "";
	const realtimeModelValue = `${agent.realtime?.provider || "openai"}/${agent.realtime?.model || "gpt-realtime"}`;
	const realtimeEffortValue = agent.realtime?.effort || "low";
	const gptLiveEnabled = agent.gptLive?.enabled === true;
	const gptLiveVoice = GPT_LIVE_VOICES.includes(
		agent.gptLive?.voice as (typeof GPT_LIVE_VOICES)[number],
	)
		? agent.gptLive!.voice!
		: GPT_LIVE_DEFAULT_VOICE;
	const supportedVoices = useMemo(() => {
		return gptLiveEnabled
			? [...GPT_LIVE_VOICES]
			: getSupportedVoicesForModel(
					agent.realtime?.provider,
					agent.realtime?.model,
				);
	}, [gptLiveEnabled, agent.realtime?.provider, agent.realtime?.model]);
	const currentVoice = useMemo(() => {
		if (gptLiveEnabled) return gptLiveVoice;
		if (
			isVoiceSupportedForModel(
				agent.voice,
				agent.realtime?.provider,
				agent.realtime?.model,
			)
		) {
			return agent.voice!;
		}
		return getDefaultVoiceForModel(
			agent.realtime?.provider,
			agent.realtime?.model,
		);
	}, [
		gptLiveEnabled,
		gptLiveVoice,
		agent.voice,
		agent.realtime?.provider,
		agent.realtime?.model,
	]);

	const timeZone = agent.timeZone || "Asia/Jerusalem";
	const timeZoneOptions = TIME_ZONE_OPTIONS.includes(timeZone)
		? TIME_ZONE_OPTIONS
		: [timeZone, ...TIME_ZONE_OPTIONS];
	const testingPhone = hasSuccessfulTestingDeployment(agent)
		? formatPhoneNumberList(testingPhoneNumbers) || "-"
		: i18n._("agentsPage.testingPhone.deployFirst");
	const livePhone = agent.phoneNumberDetails?.live?.length
		? formatPhoneNumberList(
				agent.phoneNumberDetails.live.map((phoneNumber) => phoneNumber.number),
			)
		: agent.phoneNumbers?.length
			? formatPhoneNumberList(agent.phoneNumbers)
			: "-";

	const draftVersion = useMemo(() => {
		if (
			agent.version === null ||
			agent.version === undefined ||
			Number.isNaN(agent.version)
		)
			return "-";
		return `v${agent.version}`;
	}, [agent.version]);

	const stagingVersion = useMemo(() => {
		if (
			agent.stagingDeployment?.version === null ||
			agent.stagingDeployment?.version === undefined
		)
			return "-";
		return `v${agent.stagingDeployment.version}`;
	}, [agent.stagingDeployment?.version]);

	const liveVersion = useMemo(() => {
		if (
			agent.liveDeployment?.version === null ||
			agent.liveDeployment?.version === undefined
		)
			return "-";
		return `v${agent.liveDeployment.version}`;
	}, [agent.liveDeployment?.version]);
	const draftModified = useMemo(() => {
		const stagingVersion =
			typeof agent.stagingDeployment?.version === "number"
				? agent.stagingDeployment.version
				: 0;
		return (
			!!agent.hasUncommittedChanges ||
			Number(agent.version || 0) > stagingVersion
		);
	}, [
		agent.hasUncommittedChanges,
		agent.stagingDeployment?.version,
		agent.version,
	]);

	const messages = agent.messages || [];
	const flows = agent.typicalFlows || [];
	const policySections = agent.policySections || [];
	const blacklistedCallerNumbers = agent.blacklistedCallerNumbers || [];
	const testerCallerNumbers = agent.testerCallerNumbers || [];
	const transferToHumanDestinations = useMemo(
		() => getAgentTransferDestinations(agent),
		[agent],
	);

	const addBlacklistedCallerNumber = () => {
		const normalized = normalizePhoneNumberForStorage(blacklistInput);
		if (!normalized || blacklistedCallerNumbers.includes(normalized)) {
			setBlacklistInput("");
			return;
		}

		onChange({
			...agent,
			blacklistedCallerNumbers: [...blacklistedCallerNumbers, normalized].sort(
				(a, b) => a.localeCompare(b),
			),
		});
		setBlacklistInput("");
	};

	const removeBlacklistedCallerNumber = (phoneNumber: string) => {
		onChange({
			...agent,
			blacklistedCallerNumbers: blacklistedCallerNumbers.filter(
				(value) => value !== phoneNumber,
			),
		});
	};

	const addTesterCallerNumber = () => {
		const normalized = normalizePhoneNumberForStorage(testerInput);
		if (!normalized || testerCallerNumbers.includes(normalized)) {
			setTesterInput("");
			return;
		}

		onChange({
			...agent,
			testerCallerNumbers: [...testerCallerNumbers, normalized].sort((a, b) =>
				a.localeCompare(b),
			),
		});
		setTesterInput("");
	};

	const removeTesterCallerNumber = (phoneNumber: string) => {
		onChange({
			...agent,
			testerCallerNumbers: testerCallerNumbers.filter(
				(value) => value !== phoneNumber,
			),
		});
	};

	const setTransferDestinations = (
		destinations: TransferToHumanDestination[],
	) => {
		const addresses = destinations.map((destination) => destination.address);
		onChange({
			...agent,
			transferToHumanDestinations: destinations,
			transferToHumanPhoneNumbers: addresses,
			transferToHumanPhoneNumber: addresses[0] || null,
		});
	};

	const addTransferToHumanPhoneNumber = () => {
		const normalized = normalizeTransferTargetForStorage(transferInput);
		if (
			!normalized ||
			transferToHumanDestinations.some(
				(destination) => destination.address === normalized,
			)
		) {
			setTransferInput("");
			return;
		}
		setTransferDestinations([
			...transferToHumanDestinations,
			{
				id: shortUuid("destination_"),
				type: isSipUri(normalized) ? "sip" : "pstn",
				address: normalized,
				priority: Math.max(1, Math.floor(transferPriority)),
				availability: { mode: "always" },
			},
		]);
		setTransferInput("");
	};

	const positivePriority = (value: string) => {
		const priority = Number(value);
		return Number.isInteger(priority) && priority > 0 ? priority : 1;
	};

	const updateTransferDestination = (
		id: string,
		update: (
			destination: TransferToHumanDestination,
		) => TransferToHumanDestination,
	) => {
		setTransferDestinations(
			transferToHumanDestinations.map((destination) =>
				destination.id === id ? update(destination) : destination,
			),
		);
	};

	const removeTransferToHumanPhoneNumber = (id: string) => {
		setTransferDestinations(
			transferToHumanDestinations.filter(
				(destination) => destination.id !== id,
			),
		);
	};

	const weekdayName = (day: WeeklyScheduleDay) => {
		switch (day) {
			case "sunday":
				return i18n._({ id: "weekday.sunday", message: "Sunday" });
			case "monday":
				return i18n._({ id: "weekday.monday", message: "Monday" });
			case "tuesday":
				return i18n._({ id: "weekday.tuesday", message: "Tuesday" });
			case "wednesday":
				return i18n._({ id: "weekday.wednesday", message: "Wednesday" });
			case "thursday":
				return i18n._({ id: "weekday.thursday", message: "Thursday" });
			case "friday":
				return i18n._({ id: "weekday.friday", message: "Friday" });
			case "saturday":
				return i18n._({ id: "weekday.saturday", message: "Saturday" });
		}
	};

	const weekdayShortName = (day: WeeklyScheduleDay) => {
		switch (day) {
			case "sunday":
				return i18n._({ id: "weekday.short.sunday", message: "S" });
			case "monday":
				return i18n._({ id: "weekday.short.monday", message: "M" });
			case "tuesday":
				return i18n._({ id: "weekday.short.tuesday", message: "T" });
			case "wednesday":
				return i18n._({ id: "weekday.short.wednesday", message: "W" });
			case "thursday":
				return i18n._({ id: "weekday.short.thursday", message: "T" });
			case "friday":
				return i18n._({ id: "weekday.short.friday", message: "F" });
			case "saturday":
				return i18n._({ id: "weekday.short.saturday", message: "S" });
		}
	};

	const addMessage = () => {
		onChange({
			...agent,
			messages: [
				{ id: Date.now().toString(), name: "", text: "", description: "" },
				...messages,
			],
		});
	};

	const updateMessage = (id: string, field: keyof Message, value: string) => {
		onChange({
			...agent,
			messages: messages.map((message) =>
				message.id === id ? { ...message, [field]: value } : message,
			),
		});
	};

	const removeMessage = (id: string) => {
		onChange({
			...agent,
			messages: messages.filter((message) => message.id !== id),
		});
	};

	const addFlow = () => {
		onChange({
			...agent,
			typicalFlows: [
				{ id: Date.now().toString(), name: "", description: "", example: "" },
				...flows,
			],
		});
	};

	const updateFlow = (id: string, field: keyof Flow, value: string) => {
		onChange({
			...agent,
			typicalFlows: flows.map((flow) =>
				flow.id === id ? { ...flow, [field]: value } : flow,
			),
		});
	};

	const removeFlow = (id: string) => {
		onChange({
			...agent,
			typicalFlows: flows.filter((flow) => flow.id !== id),
		});
	};

	const addPolicySection = () => {
		onChange({
			...agent,
			policySections: [
				{
					id: Date.now().toString(),
					title: isHebrew
						? `סעיף ${policySections.length + 1}`
						: `Section ${policySections.length + 1}`,
					content: "",
				},
				...policySections,
			],
		});
	};

	const updatePolicySection = (
		id: string,
		field: keyof PolicySection,
		value: string,
	) => {
		onChange({
			...agent,
			policySections: policySections.map((section) =>
				section.id === id ? { ...section, [field]: value } : section,
			),
		});
	};

	const removePolicySection = (id: string) => {
		onChange({
			...agent,
			policySections: policySections.filter((section) => section.id !== id),
		});
	};

	return (
		<div
			dir={isRtl ? "rtl" : "ltr"}
			className="h-full min-h-0 flex flex-col gap-6 overflow-hidden"
		>
			{hasNewerVersion && mode === "edit" && (
				<Alert variant="destructive" className="shrink-0">
					<AlertCircle className="h-4 w-4" />
					<AlertTitle>{i18n._("agentEditor.warning.title")}</AlertTitle>
					<AlertDescription>
						{i18n._("agentEditor.warning.newerVersion")}
					</AlertDescription>
				</Alert>
			)}
			<div className="border-[1.5px] border-card-border shadow-card rounded-lg bg-card p-4 sm:p-6">
				<div className="space-y-4">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<h2 className="text-xl font-semibold">
							{mode === "create"
								? i18n._("agentEditor.title.create")
								: i18n._("agentEditor.title.edit")}
						</h2>
						<div className="flex items-center gap-2">
							{hasSavedPlaybookChanges && isSaveDisabled && !isSaving ? (
								<Button type="button" onClick={onCancel}>
									{i18n._("common.close")}
								</Button>
							) : (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={onCancel}
										disabled={isSaving}
									>
										{i18n._("common.cancel")}
									</Button>
									<Button
										type="button"
										onClick={onSave}
										disabled={isSaveDisabled || isSaving}
									>
										{isSaving && (
											<Loader2 className="h-4 w-4 mr-2 animate-spin rtl:mr-0 rtl:ml-2" />
										)}
										{isSaving
											? mode === "create"
												? i18n._("agentEditor.creating")
												: i18n._("agentEditor.saving")
											: mode === "create"
												? i18n._("common.create")
												: i18n._("common.save")}
									</Button>
								</>
							)}
						</div>
					</div>

					<div className="flex flex-wrap items-center gap-3 text-sm">
						<Badge
							variant="outline"
							className={
								draftModified
									? "border-warn/30 bg-warn-tint text-foreground"
									: undefined
							}
						>
							{i18n._("agentEditor.status.draft")}: {draftVersion}
							{draftModified ? i18n._("agentEditor.status.modified") : ""}
						</Badge>
						<Badge variant="outline" className="bg-accent">
							{i18n._("agentEditor.status.staging")}: {stagingVersion}
						</Badge>
						<Badge variant="outline" className="bg-live-tint">
							{i18n._("agentEditor.status.production")}: {liveVersion}
						</Badge>
						<span className="inline-flex items-center gap-2 rounded-md border border-border/80 px-2.5 py-1.5">
							<Phone className="h-4 w-4 text-accent-ink" />
							<span className="font-medium">
								{i18n._("agentEditor.phone.testing")}
							</span>
							<span dir="ltr">{testingPhone}</span>
						</span>
						<span className="inline-flex items-center gap-2 rounded-md border border-border/80 px-2.5 py-1.5">
							<Phone className="h-4 w-4 text-live" />
							<span className="font-medium">
								{i18n._("agentEditor.phone.live")}
							</span>
							<span dir="ltr">{livePhone}</span>
						</span>
					</div>
					<div className="flex items-start gap-2.5 rounded-lg border border-border/80 bg-muted/40 px-3.5 py-2.5 text-sm text-muted-foreground">
						<Info className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
						<span>
							{i18n._({
								id: "agentEditor.changeEffect.reminder",
								message:
									"Changes saved here update the draft. Deploy to Testing and Live when you're ready to apply them to calls.",
							})}
						</span>
					</div>
				</div>
			</div>

			<Tabs
				dir={isRtl ? "rtl" : "ltr"}
				defaultValue="general"
				className="grid min-h-0 flex-1 gap-4 overflow-hidden lg:grid-cols-[220px_minmax(0,1fr)]"
			>
				<TabsList
					dir={isRtl ? "rtl" : "ltr"}
					className={`grid h-auto w-full ${outboundPlaybooksEnabled ? "grid-cols-2 sm:grid-cols-5" : "grid-cols-2 sm:grid-cols-4"} lg:self-start lg:flex lg:flex-col lg:items-stretch lg:gap-1 lg:bg-transparent lg:p-0`}
				>
					<TabsTrigger
						value="general"
						className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
					>
						{i18n._({
							id: "agentEditor.tabs.general",
							message: "Basics",
						})}
					</TabsTrigger>
					<TabsTrigger
						value="policies"
						className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
					>
						{i18n._("agentEditor.tabs.policies")}
					</TabsTrigger>
					<TabsTrigger
						value="flows"
						className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
					>
						{i18n._("agentEditor.tabs.flows")}
					</TabsTrigger>
					<TabsTrigger
						value="messages"
						className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
					>
						{i18n._("agentEditor.tabs.messages")}
					</TabsTrigger>
					{outboundPlaybooksEnabled && (
						<TabsTrigger
							value="outbound-playbooks"
							className="lg:flex-none lg:justify-start lg:px-4 lg:py-3 lg:whitespace-nowrap"
						>
							{i18n._({
								id: "agentEditor.tabs.outboundPlaybooks",
								message: "Outbound Playbooks",
							})}
						</TabsTrigger>
					)}
				</TabsList>

				<div className="min-h-0 min-w-0 overflow-y-auto p-1 text-start">
					<TabsContent value="general" className="space-y-6 mt-0">
						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.identity",
								message: "Set the agent’s name, voice, language, and service.",
							})}
							header={
								<>
									<h3 className="text-lg font-medium mb-4">
										{i18n._("agentEditor.sections.identity")}
									</h3>
								</>
							}
						>
							<div className="flex flex-wrap gap-4">
								<div className={`w-full sm:w-[220px] ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-name"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.name")}
									</EditorLabel>
									<Input
										id="agent-name"
										value={agent.name}
										onChange={(e) =>
											onChange({ ...agent, name: e.target.value })
										}
										placeholder={i18n._("agentEditor.placeholders.agentName")}
										{...getContentFieldProps(agent.name)}
									/>
								</div>
								<div className={`w-full sm:w-[170px] ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-voice"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.voice")}
									</EditorLabel>
									<Select
										value={currentVoice}
										onValueChange={(value) =>
											onChange(
												gptLiveEnabled
													? {
															...agent,
															gptLive: { ...agent.gptLive!, voice: value },
														}
													: { ...agent, voice: value },
											)
										}
									>
										<SelectTrigger
											id="agent-voice"
											className={editorFieldClassName}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{supportedVoices.map((voice) => (
												<SelectItem key={voice} value={voice}>
													{voice}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className={`w-full sm:w-[180px] ${fieldGroupClassName}`}>
									<EditorLabel htmlFor="agent-gender">
										{i18n._("agentEditor.fields.gender")}
									</EditorLabel>
									<Select
										value={agent.gender || "neutral"}
										onValueChange={(value) =>
											onChange({ ...agent, gender: value })
										}
									>
										<SelectTrigger id="agent-gender">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="neutral">
												{i18n._("agentEditor.options.gender.neutral")}
											</SelectItem>
											<SelectItem value="male">
												{i18n._("agentEditor.options.gender.male")}
											</SelectItem>
											<SelectItem value="female">
												{i18n._("agentEditor.options.gender.female")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className={`w-full sm:w-[180px] ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-tone"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.tone")}
									</EditorLabel>
									<Select
										value={agent.tone || "conversational"}
										onValueChange={(value) =>
											onChange({ ...agent, tone: value })
										}
									>
										<SelectTrigger
											id="agent-tone"
											className={editorFieldClassName}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="formal">
												{i18n._("agentEditor.options.tone.formal")}
											</SelectItem>
											<SelectItem value="conversational">
												{i18n._("agentEditor.options.tone.conversational")}
											</SelectItem>
											<SelectItem value="empathetic">
												{i18n._("agentEditor.options.tone.empathetic")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className={`w-full sm:w-[180px] ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-language"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.defaultLanguage")}
									</EditorLabel>
									<Select
										value={agent.defaultLanguage || "en"}
										onValueChange={(value) =>
											onChange({ ...agent, defaultLanguage: value })
										}
									>
										<SelectTrigger
											id="agent-language"
											className={editorFieldClassName}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{agent.defaultLanguage &&
												!AGENT_LANGUAGE_CODES.some(
													(code) => code === agent.defaultLanguage,
												) && (
													<SelectItem value={agent.defaultLanguage} disabled>
														{agent.defaultLanguage} (
														{i18n._("agentEditor.languages.unsupported")})
													</SelectItem>
												)}
											{AGENT_LANGUAGE_CODES.map((lang) => (
												<SelectItem key={lang} value={lang}>
													{agentLanguageLabels[lang]}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className={`w-full sm:w-[220px] ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-time-zone"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.timeZone")}
									</EditorLabel>
									<Select
										value={timeZone}
										onValueChange={(value) =>
											onChange({ ...agent, timeZone: value })
										}
									>
										<SelectTrigger
											id="agent-time-zone"
											className={editorFieldClassName}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{timeZoneOptions.map((option) => (
												<SelectItem key={option} value={option}>
													{option}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
							<div className="mt-4 flex justify-start">
								<div className={`w-full max-w-sm ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-service"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.service")}
									</EditorLabel>
									<Select
										value={agent.serviceId || "__none__"}
										onValueChange={(value) =>
											onChange({
												...agent,
												serviceId: value === "__none__" ? null : value,
											})
										}
									>
										<SelectTrigger
											id="agent-service"
											className={editorFieldClassName}
										>
											<SelectValue
												placeholder={i18n._(
													"agentEditor.placeholders.selectService",
												)}
											/>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="__none__">
												{i18n._("agentEditor.options.service.none")}
											</SelectItem>
											{services.map((service) => (
												<SelectItem key={service.id} value={service.id!}>
													{service.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</SettingsGroup>

						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.opening",
								message:
									"Define how the agent opens the call and what it needs to learn.",
							})}
							header={
								<>
									<div>
										<h3 className="font-medium text-foreground">
											{i18n._({
												id: "agentEditor.sections.openingAndPurpose",
												message: "Opening and purpose",
											})}
										</h3>
									</div>
								</>
							}
						>
							<div className={fieldGroupClassName}>
								<EditorLabel
									htmlFor="agent-greeting"
									className={fieldLabelClassName}
								>
									{i18n._("agentEditor.fields.greeting")}
								</EditorLabel>
								<Textarea
									id="agent-greeting"
									rows={3}
									value={agent.greeting || ""}
									onChange={(e) =>
										onChange({ ...agent, greeting: e.target.value })
									}
									placeholder={i18n._("agentEditor.placeholders.greeting")}
									{...getContentFieldProps(agent.greeting)}
								/>
							</div>
							<div className={fieldGroupClassName}>
								<EditorLabel
									htmlFor="agent-goal"
									className={fieldLabelClassName}
								>
									{i18n._("agentEditor.fields.informationToCollect")}
								</EditorLabel>
								<Textarea
									id="agent-goal"
									rows={3}
									value={agent.goal || ""}
									onChange={(e) => onChange({ ...agent, goal: e.target.value })}
									placeholder={i18n._(
										"agentEditor.placeholders.informationToCollect",
									)}
									{...getContentFieldProps(agent.goal)}
								/>
							</div>
						</SettingsGroup>

						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.transfer",
								message:
									"Choose where to transfer callers and when each destination is available.",
							})}
							header={
								<span
									dir={isRtl ? "rtl" : "ltr"}
									className={`block w-full ${isRtl ? "text-right" : "text-left"}`}
								>
									<span>
										{i18n._({
											id: "agentEditor.sections.transferToPerson",
											message: "Transfer to a person",
										})}
									</span>
									<span
										className={`${isRtl ? "mr-2" : "ml-2"} text-xs font-normal text-muted-foreground`}
									>
										{i18n._({ id: "common.optional", message: "Optional" })}
									</span>
								</span>
							}
							collapsible
						>
							<div className={`mt-4 ${fieldGroupClassName}`}>
								<EditorLabel
									htmlFor="agent-transfer-to-human-input"
									className={fieldLabelClassName}
								>
									{i18n._("agentEditor.fields.transferToHumanNumber")}
								</EditorLabel>
								<p className="text-xs text-muted-foreground">
									{i18n._({
										id: "agentEditor.transferDestinations.help",
										message:
											"Phone numbers or SIP addresses. Lower priority numbers are tried first.",
									})}
								</p>
								{getAgentInboundAnsweringPolicy(agent) === "AI_ONLY" && (
									<Alert>
										<AlertCircle className="h-4 w-4" />
										<AlertDescription>
											{i18n._({
												id: "agentEditor.transferDestinations.aiOnlyDisabled",
												message:
													"Transfer to human is disabled while the inbound answering policy is AI only.",
											})}
										</AlertDescription>
									</Alert>
								)}
								<div className="flex flex-col gap-2 sm:flex-row">
									<Input
										id="agent-transfer-to-human-input"
										value={transferInput}
										onChange={(event) => setTransferInput(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												event.preventDefault();
												addTransferToHumanPhoneNumber();
											}
										}}
										placeholder={i18n._(
											"agentEditor.placeholders.transferToHumanNumber",
										)}
										dir="ltr"
										className={`${editorFieldClassName} text-left`}
									/>
									<div className="flex items-center gap-2">
										<label
											htmlFor="agent-transfer-priority-input"
											className="text-sm text-muted-foreground"
										>
											{i18n._({
												id: "agentEditor.transferDestinations.priority",
												message: "Priority",
											})}
										</label>
										<Input
											id="agent-transfer-priority-input"
											type="number"
											min={1}
											step={1}
											value={transferPriority}
											onChange={(event) =>
												setTransferPriority(
													positivePriority(event.target.value),
												)
											}
											dir="ltr"
											className="w-20 text-left"
										/>
									</div>
									<Button
										type="button"
										variant="outline"
										onClick={addTransferToHumanPhoneNumber}
										disabled={!transferInput.trim()}
									>
										<Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
										{i18n._("agentEditor.actions.addTransferDestination")}
									</Button>
								</div>
								<div className="space-y-3">
									{transferToHumanDestinations.length === 0 ? (
										<p className="text-sm text-muted-foreground">
											{i18n._("agentEditor.empty.transferDestinations")}
										</p>
									) : (
										transferToHumanDestinations.map((destination) => (
											<div
												key={destination.id}
												className="border-t border-border pt-4 space-y-3"
											>
												<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
													<Input
														type="number"
														min={1}
														step={1}
														value={destination.priority}
														onChange={(event) =>
															updateTransferDestination(
																destination.id,
																(current) => ({
																	...current,
																	priority: positivePriority(
																		event.target.value,
																	),
																}),
															)
														}
														aria-label={i18n._({
															id: "agentEditor.transferDestinations.priority",
															message: "Priority",
														})}
														dir="ltr"
														className="w-20 text-left"
													/>
													<span
														className="min-w-0 flex-1 break-all text-sm"
														dir="ltr"
													>
														{destination.type === "pstn"
															? formatPhoneNumber(destination.address)
															: destination.address}
													</span>
													<Select
														value={destination.availability.mode}
														onValueChange={(mode: "always" | "custom") =>
															updateTransferDestination(
																destination.id,
																(current) => ({
																	...current,
																	availability:
																		mode === "always"
																			? { mode: "always" }
																			: {
																					mode: "custom",
																					windows: [
																						DEFAULT_AVAILABILITY_WINDOW,
																					],
																				},
																}),
															)
														}
													>
														<SelectTrigger className="w-44">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="always">
																{i18n._({
																	id: "availability.always",
																	message: "Always",
																})}
															</SelectItem>
															<SelectItem value="custom">
																{i18n._({
																	id: "availability.custom",
																	message: "Custom",
																})}
															</SelectItem>
														</SelectContent>
													</Select>
													<Button
														type="button"
														variant="ghost"
														size="icon"
														onClick={() =>
															removeTransferToHumanPhoneNumber(destination.id)
														}
														aria-label={i18n._(
															"agentEditor.actions.removeTransferDestination",
														)}
													>
														<X className="h-4 w-4" />
													</Button>
												</div>

												{destination.availability.mode === "custom" && (
													<div className="space-y-3 rounded-md bg-secondary p-3">
														<p className="text-xs text-muted-foreground">
															{i18n._({
																id: "availability.timeZone",
																message:
																	"Times use the agent timezone: {timeZone}",
																values: { timeZone },
															})}
														</p>
														{destination.availability.windows.map(
															(window, windowIndex) => (
																<div
																	key={`${destination.id}-${windowIndex}`}
																	className="space-y-2 border-b border-border pb-3 last:border-0 last:pb-0"
																>
																	<div className="flex flex-wrap gap-1">
																		{WEEKDAYS.map((day) => (
																			<Button
																				key={day}
																				type="button"
																				size="sm"
																				variant={
																					window.days.includes(day)
																						? "default"
																						: "outline"
																				}
																				onClick={() =>
																					updateTransferDestination(
																						destination.id,
																						(current) => {
																							if (
																								current.availability.mode !==
																								"custom"
																							)
																								return current;
																							return {
																								...current,
																								availability: {
																									...current.availability,
																									windows:
																										current.availability.windows.map(
																											(item, index) =>
																												index === windowIndex
																													? {
																															...item,
																															days: item.days.includes(
																																day,
																															)
																																? item.days.filter(
																																		(value) =>
																																			value !==
																																			day,
																																	)
																																: [
																																		...item.days,
																																		day,
																																	],
																														}
																													: item,
																										),
																								},
																							};
																						},
																					)
																				}
																				aria-label={weekdayName(day)}
																				title={weekdayName(day)}
																			>
																				{weekdayShortName(day)}
																			</Button>
																		))}
																	</div>
																	<div className="flex flex-wrap items-center gap-2">
																		<Input
																			type="time"
																			aria-label={i18n._({
																				id: "availability.startTime",
																				message: "Start time",
																			})}
																			value={window.startTime}
																			onChange={(event) =>
																				updateTransferDestination(
																					destination.id,
																					(current) =>
																						current.availability.mode ===
																						"custom"
																							? {
																									...current,
																									availability: {
																										...current.availability,
																										windows:
																											current.availability.windows.map(
																												(item, index) =>
																													index === windowIndex
																														? {
																																...item,
																																startTime:
																																	event.target
																																		.value,
																															}
																														: item,
																											),
																									},
																								}
																							: current,
																				)
																			}
																			dir="ltr"
																			className="w-32 text-left"
																		/>
																		<span aria-hidden="true">–</span>
																		<Input
																			type="time"
																			aria-label={i18n._({
																				id: "availability.endTime",
																				message: "End time",
																			})}
																			value={window.endTime}
																			onChange={(event) =>
																				updateTransferDestination(
																					destination.id,
																					(current) =>
																						current.availability.mode ===
																						"custom"
																							? {
																									...current,
																									availability: {
																										...current.availability,
																										windows:
																											current.availability.windows.map(
																												(item, index) =>
																													index === windowIndex
																														? {
																																...item,
																																endTime:
																																	event.target
																																		.value,
																															}
																														: item,
																											),
																									},
																								}
																							: current,
																				)
																			}
																			dir="ltr"
																			className="w-32 text-left"
																		/>
																		<Button
																			type="button"
																			variant="ghost"
																			size="icon"
																			onClick={() =>
																				updateTransferDestination(
																					destination.id,
																					(current) =>
																						current.availability.mode ===
																						"custom"
																							? {
																									...current,
																									availability: {
																										...current.availability,
																										windows:
																											current.availability.windows.filter(
																												(_, index) =>
																													index !== windowIndex,
																											),
																									},
																								}
																							: current,
																				)
																			}
																			aria-label={i18n._({
																				id: "availability.removeWindow",
																				message: "Remove availability window",
																			})}
																		>
																			<X className="h-4 w-4" />
																		</Button>
																	</div>
																</div>
															),
														)}
														<Button
															type="button"
															variant="link"
															className="h-auto p-0"
															onClick={() =>
																updateTransferDestination(
																	destination.id,
																	(current) =>
																		current.availability.mode === "custom"
																			? {
																					...current,
																					availability: {
																						...current.availability,
																						windows: [
																							...current.availability.windows,
																							{
																								...DEFAULT_AVAILABILITY_WINDOW,
																								days: [
																									...DEFAULT_AVAILABILITY_WINDOW.days,
																								],
																							},
																						],
																					},
																				}
																			: current,
																)
															}
														>
															<Plus className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
															{i18n._({
																id: "availability.addWindow",
																message: "Add time window",
															})}
														</Button>
													</div>
												)}
											</div>
										))
									)}
								</div>
							</div>
						</SettingsGroup>
						{canEditAdminFields ? (
							<SettingsGroup
								description={i18n._({
									id: "agentEditor.groupHelp.advanced",
									message:
										"Configure model options, testing access, and caller restrictions.",
								})}
								header={
									<span
										dir={isRtl ? "rtl" : "ltr"}
										className={`block w-full ${isRtl ? "text-right" : "text-left"}`}
									>
										<span>
											{i18n._({
												id: "agentEditor.sections.advanced",
												message: "Advanced settings",
											})}
										</span>
										<span
											className={`${isRtl ? "mr-2" : "ml-2"} text-xs font-normal text-muted-foreground`}
										>
											{i18n._({
												id: "agentEditor.sections.companyAdminsOnly",
												message: "Company admins only",
											})}
										</span>
									</span>
								}
								collapsible
							>
								<div className="mt-4 flex items-start justify-between gap-4 border-b border-border pb-4">
									<div className="text-start">
										<EditorLabel htmlFor="agent-gpt-live-enabled">
											{i18n._({
												id: "agentEditor.fields.gptLiveEnabled",
												message: "Enable GPTLive",
											})}
										</EditorLabel>
										<p className="mt-1 text-sm text-muted-foreground">
											{i18n._({
												id: "agentEditor.help.gptLive",
												message:
													"Use GPTLive for receptionist calls and all outbound playbooks of this agent.",
											})}
										</p>
									</div>
									<Switch
										id="agent-gpt-live-enabled"
										checked={gptLiveEnabled}
										onCheckedChange={(enabled) =>
											onChange({
												...agent,
												gptLive: {
													enabled,
													voice: gptLiveVoice,
													reasoningModel:
														agent.gptLive?.reasoningModel ||
														GPT_LIVE_DEFAULT_REASONING_MODEL,
												},
											})
										}
									/>
								</div>
								<div className="mt-4 grid gap-4 border-b border-border pb-4 sm:grid-cols-2 lg:grid-cols-3">
									{gptLiveEnabled ? (
										<div className={fieldGroupClassName}>
											<EditorLabel htmlFor="agent-gpt-live-reasoning-model">
												{i18n._({
													id: "agentEditor.fields.gptLiveReasoningModel",
													message: "Backend reasoning model",
												})}
											</EditorLabel>
											<Input
												id="agent-gpt-live-reasoning-model"
												value={
													agent.gptLive?.reasoningModel ||
													GPT_LIVE_DEFAULT_REASONING_MODEL
												}
												onChange={(event) =>
													onChange({
														...agent,
														gptLive: {
															...agent.gptLive!,
															reasoningModel: event.target.value,
														},
													})
												}
												placeholder={GPT_LIVE_DEFAULT_REASONING_MODEL}
												className={editorFieldClassName}
											/>
										</div>
									) : (
										<>
											<div className={fieldGroupClassName}>
												<EditorLabel htmlFor="agent-model">
													{i18n._("agentEditor.fields.realtimeModel")}
												</EditorLabel>
												<Select
													value={realtimeModelValue}
													onValueChange={(value) => {
														const selected = REALTIME_MODEL_OPTIONS.find(
															(option) => option.value === value,
														);
														if (!selected) return;
														const newProvider = selected.realtime.provider;
														const newModel = selected.realtime.model;
														const nextVoice = isVoiceSupportedForModel(
															agent.voice,
															newProvider,
															newModel,
														)
															? agent.voice
															: getDefaultVoiceForModel(newProvider, newModel);
														onChange({
															...agent,
															voice: nextVoice,
															realtime: {
																...selected.realtime,
																effort: agent.realtime?.effort || "low",
															},
														});
													}}
												>
													<SelectTrigger id="agent-model">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{REALTIME_MODEL_OPTIONS.map((option) => (
															<SelectItem
																key={option.value}
																value={option.value}
															>
																{option.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>
											{(agent.realtime?.provider || "openai") === "openai" ? (
												<div className={fieldGroupClassName}>
													<EditorLabel htmlFor="agent-realtime-effort">
														{i18n._("agentEditor.fields.realtimeEffort")}
													</EditorLabel>
													<Select
														value={realtimeEffortValue}
														onValueChange={(value) => {
															const effort = REALTIME_EFFORT_OPTIONS.find(
																(option) => option === value,
															);
															if (!effort) return;
															onChange({
																...agent,
																realtime: {
																	provider:
																		agent.realtime?.provider || "openai",
																	model:
																		agent.realtime?.model || "gpt-realtime",
																	effort,
																},
															});
														}}
													>
														<SelectTrigger id="agent-realtime-effort">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															{REALTIME_EFFORT_OPTIONS.map((effort) => (
																<SelectItem key={effort} value={effort}>
																	{effort}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											) : null}
										</>
									)}
								</div>
								<div className={`mt-4 ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-blacklist"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.blacklistedCallers")}
									</EditorLabel>
									<div className="flex flex-col gap-2 sm:flex-row">
										<Input
											id="agent-blacklist"
											value={blacklistInput}
											onChange={(e) => setBlacklistInput(e.target.value)}
											onKeyDown={(event) => {
												if (event.key === "Enter") {
													event.preventDefault();
													if (canEditAdminFields) addBlacklistedCallerNumber();
												}
											}}
											placeholder={i18n._(
												"agentEditor.placeholders.blacklistedCaller",
											)}
											disabled={!canEditAdminFields}
											dir="ltr"
											className={`${fieldClassName} ${isRtl ? "text-right" : "text-left"}`}
										/>
										<Button
											type="button"
											variant="outline"
											onClick={addBlacklistedCallerNumber}
											disabled={!canEditAdminFields || !blacklistInput.trim()}
										>
											<Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{i18n._("agentEditor.actions.addCaller")}
										</Button>
									</div>
									<div className="flex flex-wrap gap-2">
										{blacklistedCallerNumbers.length === 0 ? (
											<p className="text-sm text-muted-foreground">
												{i18n._("agentEditor.empty.blacklistedCallers")}
											</p>
										) : (
											blacklistedCallerNumbers.map((phoneNumber) => (
												<Badge
													key={phoneNumber}
													variant="outline"
													className="flex items-center gap-1 px-2 py-1"
												>
													<span dir="ltr">
														{formatPhoneNumber(phoneNumber)}
													</span>
													{canEditAdminFields && (
														<button
															type="button"
															onClick={() =>
																removeBlacklistedCallerNumber(phoneNumber)
															}
															className="rounded-sm text-muted-foreground hover:text-foreground"
															aria-label={i18n._(
																"agentEditor.actions.removeCaller",
															)}
														>
															<X className="h-3 w-3" />
														</button>
													)}
												</Badge>
											))
										)}
									</div>
								</div>
								<div className={fieldGroupClassName}>
									<EditorLabel
										htmlFor="agent-testers"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.testerNumbers")}
									</EditorLabel>
									<div className="flex flex-col gap-2 sm:flex-row">
										<Input
											id="agent-testers"
											value={testerInput}
											onChange={(e) => setTesterInput(e.target.value)}
											onKeyDown={(event) => {
												if (event.key === "Enter") {
													event.preventDefault();
													addTesterCallerNumber();
												}
											}}
											placeholder={i18n._(
												"agentEditor.placeholders.testerNumber",
											)}
											dir="ltr"
											className={`${fieldClassName} ${isRtl ? "text-right" : "text-left"}`}
										/>
										<Button
											type="button"
											variant="outline"
											onClick={addTesterCallerNumber}
											disabled={!testerInput.trim()}
										>
											<Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{i18n._("agentEditor.actions.addTester")}
										</Button>
									</div>
									<div className="flex flex-wrap gap-2">
										{testerCallerNumbers.length === 0 ? (
											<p className="text-sm text-muted-foreground">
												{i18n._("agentEditor.empty.testerNumbers")}
											</p>
										) : (
											testerCallerNumbers.map((phoneNumber) => (
												<Badge
													key={phoneNumber}
													variant="outline"
													className="flex items-center gap-1 px-2 py-1"
												>
													<span dir="ltr">
														{formatPhoneNumber(phoneNumber)}
													</span>
													<button
														type="button"
														onClick={() =>
															removeTesterCallerNumber(phoneNumber)
														}
														className="rounded-sm text-muted-foreground hover:text-foreground"
														aria-label={i18n._(
															"agentEditor.actions.removeTester",
														)}
													>
														<X className="h-3 w-3" />
													</button>
												</Badge>
											))
										)}
									</div>
								</div>
							</SettingsGroup>
						) : null}
					</TabsContent>

					<TabsContent value="policies" className="space-y-6 mt-0">
						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.policies",
								message:
									"Define what the agent should do and what it must avoid.",
							})}
							header={
								<>
									<h3>{i18n._("agentEditor.tabs.policies")}</h3>
								</>
							}
						>
							<div
								className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${isRtl ? "sm:[direction:rtl]" : ""}`}
							>
								<div className={fieldGroupClassName}>
									<EditorLabel
										htmlFor="agent-dos"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.do")}
									</EditorLabel>
									<Textarea
										id="agent-dos"
										rows={8}
										value={agent.dos || ""}
										onChange={(e) =>
											onChange({ ...agent, dos: e.target.value })
										}
										placeholder={i18n._("agentEditor.placeholders.do")}
										{...getContentFieldProps(agent.dos)}
									/>
								</div>
								<div className={fieldGroupClassName}>
									<EditorLabel
										htmlFor="agent-donts"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.dont")}
									</EditorLabel>
									<Textarea
										id="agent-donts"
										rows={8}
										value={agent.donts || ""}
										onChange={(e) =>
											onChange({ ...agent, donts: e.target.value })
										}
										placeholder={i18n._("agentEditor.placeholders.dont")}
										{...getContentFieldProps(agent.donts)}
									/>
								</div>
							</div>
						</SettingsGroup>

						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.additionalPolicies",
								message:
									"Add business-specific instructions, grouped by topic.",
							})}
							header={
								<>
									<div className="flex items-center justify-between">
										<h4 className="font-medium">
											{i18n._("agentEditor.sections.additionalPolicies")}
										</h4>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addPolicySection}
										>
											<Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{i18n._("agentEditor.actions.addSection")}
										</Button>
									</div>
								</>
							}
						>
							{policySections.map((section) => (
								<div
									key={section.id}
									className="border-t border-border pt-4 space-y-2 first:border-t-0 first:pt-0"
								>
									<div className="flex items-center gap-2">
										<Input
											value={section.title}
											onChange={(e) =>
												updatePolicySection(section.id, "title", e.target.value)
											}
											placeholder={i18n._(
												"agentEditor.placeholders.sectionTitle",
											)}
											{...getContentFieldProps(section.title)}
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => removePolicySection(section.id)}
										>
											<X className="w-4 h-4" />
										</Button>
									</div>
									<Textarea
										rows={4}
										value={section.content}
										onChange={(e) =>
											updatePolicySection(section.id, "content", e.target.value)
										}
										placeholder={i18n._(
											"agentEditor.placeholders.sectionContent",
										)}
										{...getContentFieldProps(section.content)}
									/>
								</div>
							))}
						</SettingsGroup>
					</TabsContent>

					<TabsContent value="flows" className="space-y-4 mt-0">
						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.flows",
								message:
									"Describe how the agent should handle common conversations.",
							})}
							header={
								<>
									<div className="flex items-center justify-between">
										<h4 className="font-medium">
											{i18n._("agentEditor.sections.conversationFlows")}
										</h4>
										<Button
											type="button"
											variant="outline"
											onClick={addFlow}
											size="sm"
										>
											<Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{i18n._("agentEditor.actions.addFlow")}
										</Button>
									</div>
								</>
							}
						>
							{flows.length === 0 && (
								<p className="text-sm text-muted-foreground">
									{i18n._("agentEditor.empty.flows")}
								</p>
							)}
							{flows.map((flow) => (
								<div
									key={flow.id}
									className="border-t border-border pt-4 space-y-2 first:border-t-0 first:pt-0"
								>
									<div className="flex items-center gap-2">
										<Input
											value={flow.name}
											onChange={(e) =>
												updateFlow(flow.id, "name", e.target.value)
											}
											placeholder={i18n._("agentEditor.placeholders.flowName")}
											{...getContentFieldProps(flow.name)}
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => removeFlow(flow.id)}
										>
											<X className="w-4 h-4" />
										</Button>
									</div>
									<Textarea
										rows={3}
										value={flow.description}
										onChange={(e) =>
											updateFlow(flow.id, "description", e.target.value)
										}
										placeholder={i18n._(
											"agentEditor.placeholders.flowDescription",
										)}
										{...getContentFieldProps(flow.description)}
									/>
									<Textarea
										rows={5}
										value={flow.example}
										onChange={(e) =>
											updateFlow(flow.id, "example", e.target.value)
										}
										placeholder={i18n._("agentEditor.placeholders.flowExample")}
										{...getContentFieldProps(flow.example, "font-mono text-sm")}
									/>
								</div>
							))}
						</SettingsGroup>
					</TabsContent>

					<TabsContent value="messages" className="space-y-4 mt-0">
						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.sender",
								message: "Set the sender name used for outgoing messages.",
							})}
							header={
								<>
									<h3>{i18n._("agentEditor.fields.senderName")}</h3>
								</>
							}
						>
							<div className="flex justify-start">
								<div className={`w-full max-w-md ${fieldGroupClassName}`}>
									<EditorLabel
										htmlFor="agent-sender-name"
										className={fieldLabelClassName}
									>
										{i18n._("agentEditor.fields.senderName")}
									</EditorLabel>
									<Input
										id="agent-sender-name"
										maxLength={11}
										value={agent.messagesSenderName || ""}
										onChange={(e) =>
											onChange({
												...agent,
												messagesSenderName: e.target.value.slice(0, 11),
											})
										}
										placeholder={i18n._("agentEditor.placeholders.senderName")}
										{...getContentFieldProps(agent.messagesSenderName)}
									/>
								</div>
							</div>
						</SettingsGroup>
						<SettingsGroup
							description={i18n._({
								id: "agentEditor.groupHelp.messages",
								message:
									"Create reusable messages the agent can send during calls.",
							})}
							header={
								<>
									<div className="flex items-center justify-between">
										<h4 className="font-medium">
											{i18n._("agentEditor.sections.predefinedMessages")}
										</h4>
										<Button
											type="button"
											variant="outline"
											onClick={addMessage}
											size="sm"
										>
											<Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
											{i18n._("agentEditor.actions.addMessage")}
										</Button>
									</div>
								</>
							}
						>
							{messages.length === 0 && (
								<p className="text-sm text-muted-foreground">
									{i18n._("agentEditor.empty.messages")}
								</p>
							)}
							{messages.map((message) => (
								<div
									key={message.id}
									className="border-t border-border pt-4 space-y-2 first:border-t-0 first:pt-0"
								>
									<div className="flex items-center gap-2">
										<Input
											value={message.name}
											onChange={(e) =>
												updateMessage(message.id, "name", e.target.value)
											}
											placeholder={i18n._(
												"agentEditor.placeholders.messageName",
											)}
											{...getContentFieldProps(message.name)}
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => removeMessage(message.id)}
										>
											<X className="w-4 h-4" />
										</Button>
									</div>
									<Textarea
										rows={3}
										value={message.text}
										onChange={(e) =>
											updateMessage(message.id, "text", e.target.value)
										}
										placeholder={i18n._("agentEditor.placeholders.messageText")}
										{...getContentFieldProps(message.text)}
									/>
									<Input
										value={message.description}
										onChange={(e) =>
											updateMessage(message.id, "description", e.target.value)
										}
										placeholder={i18n._("agentEditor.placeholders.whenToUse")}
										{...getContentFieldProps(message.description)}
									/>
								</div>
							))}
						</SettingsGroup>
					</TabsContent>
					{outboundPlaybooksEnabled && (
						<TabsContent value="outbound-playbooks" className="mt-0">
							<OutboundPlaybooksPanel
								createButtonVariant="outline"
								agent={agent}
								onSavedChange={() => setHasSavedPlaybookChanges(true)}
							/>
						</TabsContent>
					)}
				</div>
			</Tabs>
		</div>
	);
}
